// src/api/kailux.js
// ══════════════════════════════════════════════════════════════════════════════
// KAILUX API CLIENT  v7.0 — DEFINITIVE CONSOLIDATION
// Remediation R1–R10
// Pillar A  (Offline & Resilience — snapshots, health check)
// Pillar B  (Admin Board — B1 Plans · B2 Firms/Support · B3 System/Flags)
// Pillar D  (Source Document Enrichment — D1 uploads/badges · D2 letters)
// Pillar E  (Practice Workflow — E1 Intake · E2 Reconciliation · E3 Calendar · E4 Batch)
// Pillar F  (Recurring Revenue — F1 Management Accounts · F2 Add-on Pricing)
// ══════════════════════════════════════════════════════════════════════════════
import axios from 'axios'
import { supabase } from '../supabase'
import {
  snapshotClients,
  snapshotAFSHistory,
  snapshotFirmName,
} from '../utils/offlineSnapshot'
import { emitErrorToast, makeClientErrorId } from '../utils/errorToastBus'

// Sep 2026 — see main.py's global error-sanitization block for the backend
// half of this. Only infrastructure-level failures get the generic toast:
// the person can't self-serve out of a 500 or a dropped connection, but a
// 4xx (validation, "not found", auth) already has its own inline handling
// on the page that triggered it, and re-surfacing that as a toast too would
// just be noisy and duplicate. 401 is excluded because it triggers an
// immediate redirect to /login below — a toast would just flash and vanish.
export function shouldToastError(error) {
  // Best-effort requests (a page that has a built-in fallback and loses
  // nothing when the call fails, e.g. the Signup page's live prices) pass
  // { silent: true } so a flaky connection doesn't greet a visitor with an
  // error toast about something they never asked for.
  if (error.config?.silent) return false
  if (!error.response) return true          // network failure / CORS / timeout / DNS
  return error.response.status >= 500
}

function toastForError(error) {
  const backendErrorId = error.response?.data?.error_id
  const message = error.response
    ? 'A request to our servers failed to complete. You can try again.'
    : "We couldn't reach the KaiLux servers. Check your connection and try again."
  emitErrorToast({ message, errorId: backendErrorId || makeClientErrorId() })
}

const BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://kailux-platform-production.up.railway.app'

// ── Authenticated API instance ────────────────────────────────────────────────
const API = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
})

// Request: attach Supabase session token
API.interceptors.request.use(async (config) => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`
  }
  return config
})

// Oct 2026 request-storm fix (see kailux-fixes-log.md). A 401 used to do an
// unconditional hard `window.location.href = '/login'` -- from every parallel
// request that failed, with no check that the session was actually gone. A
// single transient 401 (a request that raced a token refresh and went out with
// no/stale Authorization header) therefore threw a signed-in user off the page
// and reloaded the whole app, and during the auth-event loop it did so over and
// over. Now: retry once with whatever session exists *now*; only when the
// session is genuinely gone, or the retry is still rejected, sign out and go to
// /login -- once, however many requests failed together.
let _redirectingToLogin = false
function redirectToLogin() {
  if (_redirectingToLogin) return
  _redirectingToLogin = true
  window.location.href = '/login'
}

export async function handleUnauthorized(error, deps) {
  const { getSession, signOut, retry, redirect, onLoginPage } = deps
  const config = error.config
  try {
    const { data: { session } } = await getSession()
    if (session?.access_token && config && !config._authRetried) {
      // Request interceptor re-reads the session and attaches a fresh token.
      return await retry({ ...config, _authRetried: true })
    }
    if (session?.access_token) {
      // Same request rejected twice with a live session: the session really
      // is no good. Clear it so /login doesn't bounce straight back.
      try { await signOut() } catch { /* ignore */ }
    }
  } catch (retryErr) {
    // A retry that fails for any reason other than another 401 is that
    // request's own failure, not an auth failure -- surface it as-is.
    if (retryErr?.response && retryErr.response.status !== 401) throw retryErr
  }
  if (!onLoginPage()) redirect()
  return Promise.reject(error)
}

// Response: redirect on 401, write offline snapshots for key endpoints
API.interceptors.response.use(
  (response) => {
    const url = response.config?.url || ''

    if (url.includes('/api/clients') && !url.includes('/afs-jobs')) {
      const data = response.data
      if (Array.isArray(data)) snapshotClients(data)
    }
    if (url.includes('/api/afs-jobs')) {
      const data = response.data
      if (Array.isArray(data)) snapshotAFSHistory(data)
    }
    if (url.match(/\/api\/firms\/[^/]+$/) && response.data?.name) {
      snapshotFirmName(response.data.name)
    }

    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      return handleUnauthorized(error, {
        getSession: () => supabase.auth.getSession(),
        signOut:    () => supabase.auth.signOut(),
        retry:      (config) => API(config),
        redirect:   redirectToLogin,
        onLoginPage: () => window.location.pathname === '/login',
      })
    }
    if (shouldToastError(error)) {
      toastForError(error)
    }
    return Promise.reject(error)
  }
)

// ── Public API instance (no auth) ─────────────────────────────────────────────
// Used for token-based portals where no Supabase session exists:
// director sign-off (/signoff/:token) and client intake (/intake/:token).
const PublicAPI = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
})

// No 401-redirect concern here (these portals have no session to expire),
// but the same infrastructure-failure toast still applies — a director
// mid-signoff hitting a dropped connection deserves the same "try again,
// here's a reference" treatment as internal staff.
PublicAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldToastError(error)) {
      toastForError(error)
    }
    return Promise.reject(error)
  }
)

// ── Helper: get bearer token for raw XHR/fetch calls ──────────────────────────
// Used by D1 upload (raw XHR, for progress events) and HelpChat (raw fetch,
// streams-friendly without axios interceptor overhead).
export const getBearerToken = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token || ''
}


// ══════════════════════════════════════════════════════════════════════════════
// AUTH
// ══════════════════════════════════════════════════════════════════════════════
export const registerFirm   = (data) => API.post('/api/auth/register', data)
export const verifySignupCode = (firmId, otp) => API.post('/api/auth/verify-signup-code', { firm_id: firmId, otp })
export const resendSignupCode = (firmId) => API.post('/api/auth/resend-signup-code', { firm_id: firmId })
export const verifyEmailLink = (token) => API.get(`/api/auth/verify-email-link/${token}`).then((r) => r.data)
// Self-service recovery for the orphaned-auth-user shape (5 Sept 2026 —
// see kailux-fixes-log.md and kailux-master-plan.md item 16): register_firm
// creates the Supabase Auth user before the firms row, so a failure in
// between used to leave a real, logged-in-able account with no firm
// anywhere and no way to fix it. Called from IncompleteSignup.jsx, which
// ProtectedRoute routes to whenever `user` is set but `firm` is null.
// Uses the authenticated `API` instance deliberately — the whole point is
// that a real (if incomplete) Supabase session exists to prove ownership.
export const resetIncompleteSignup = () => API.post('/api/auth/reset-incomplete-signup')
export const checkFirmName  = (name) => PublicAPI.get(`/api/firms/check-name?name=${encodeURIComponent(name)}`)
export const requestPasswordReset = (email) =>
  PublicAPI.post('/api/auth/request-password-reset', { email }).then((r) => r.data)


// ══════════════════════════════════════════════════════════════════════════════
// FIRMS
// ══════════════════════════════════════════════════════════════════════════════
export const getFirm        = (id)         => API.get(`/api/firms/${id}`)
export const updateFirm     = (id, data)   => API.put(`/api/firms/${id}`, data)
export const inviteTeamMember = (firmId, email, role='member') =>
  API.post(`/api/firms/${firmId}/invite-team-member`, { email, role })
export const listTeamMembers = (firmId) =>
  API.get(`/api/firms/${firmId}/team-members`).then((r) => r.data)
export const removeTeamMember = (firmId, memberId) =>
  API.delete(`/api/firms/${firmId}/team-members/${memberId}`).then((r) => r.data)

export const updateTeamMember = (firmId, memberId, data) =>
  API.patch(`/api/firms/${firmId}/team-members/${memberId}`, data).then((r) => r.data)
export const acceptTeamInvite = (token, password) =>
  PublicAPI.post('/api/team/accept-invite', { token, password }).then((r) => r.data)
export const listFirmInvoices = (firmId) =>
  API.get(`/api/firms/${firmId}/invoices`).then((r) => r.data)
export const getFirmActivityLog = (firmId) =>
  API.get(`/api/firms/${firmId}/activity-log`).then((r) => r.data)
export const exportFirmData = (firmId) =>
  API.post(`/api/firms/${firmId}/export-data`).then((r) => r.data)
export const deleteFirmAccount = (firmId, confirmation) =>
  API.post(`/api/firms/${firmId}/delete-account`, { confirmation }).then((r) => r.data)

// ══════════════════════════════════════════════════════════════════════════════
// PLATFORM ADMINS (KaiLux-internal staff — admin.kailux.co.za)
// ══════════════════════════════════════════════════════════════════════════════
export const listPlatformAdmins = () =>
  API.get('/api/admin/platform-admins').then((r) => r.data)
export const invitePlatformAdmin = (email) =>
  API.post('/api/admin/platform-admins/invite', { email }).then((r) => r.data)
export const revokePlatformAdmin = (adminId) =>
  API.delete(`/api/admin/platform-admins/${adminId}`).then((r) => r.data)
export const acceptAdminInvite = (token, password) =>
  PublicAPI.post('/api/admin/platform-admins/accept-invite', { token, password }).then((r) => r.data)

export const uploadFirmLogo = (firmId, file) => {
  const formData = new FormData()
  formData.append('file', file)
  return API.post(`/api/firms/${firmId}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadFirmLetterhead = (firmId, file) => {
  const formData = new FormData()
  formData.append('file', file)
  return API.post(`/api/firms/${firmId}/letterhead`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadPracticeNumberDocuments = (firmId, files) => {
  const formData = new FormData()
  files.forEach((f) => formData.append('files', f))
  return API.post(`/api/firms/${firmId}/practice-number-documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data)
}


// ══════════════════════════════════════════════════════════════════════════════
// CLIENTS
// ══════════════════════════════════════════════════════════════════════════════
export const getClients   = (firmId)       => API.get(`/api/clients?firm_id=${firmId}`)
export const createClient = (data)         => API.post('/api/clients', data)
export const getClient    = (id)           => API.get(`/api/clients/${id}`)
export const updateClient = (id, data)     => API.put(`/api/clients/${id}`, data)


// ══════════════════════════════════════════════════════════════════════════════
// AFS JOBS
// ══════════════════════════════════════════════════════════════════════════════
export const getAFSJobs       = (firmId)   => API.get(`/api/afs-jobs?firm_id=${firmId}`)
export const createAFSJob     = (data)     => API.post('/api/afs-jobs', data)
export const getClientAFSJobs = (clientId) => API.get(`/api/clients/${clientId}/afs-jobs`)
// Read-only check called before starting/continuing an AFS prep flow, so the
// Single-sourcing fix (25 Jul 2026) — TrialBalance.jsx's live mapping-review
// preview now calls this instead of keeping its own JS reimplementation of
// the IFRS category aggregation. Same function the backend uses
// authoritatively for real AFS generation and AI note drafting
// (aggregate_figures_from_accounts in main.py) — see that endpoint's
// docstring for the drift risk this closes. Pure computation, no DB access.
export const aggregateFigures = (accounts, mappings) =>
  API.post('/api/trial-balance/aggregate', { accounts, mappings })

// 9 Sep 2026 — preview for the "Exclude" flow on a flagged control/suspense
// row: does removing it leave the trial balance in balance? See that
// endpoint's docstring in main.py for why this must be a hard stop (not an
// overridable warning) when the answer is no.
export const checkTrialBalanceExclusion = (accounts, code, mappings = {}) =>
  API.post('/api/trial-balance/check-exclusion', { accounts, code, mappings })

// UI can warn if one already exists for this client + financial year (Phase 6,
// July 2026 batch, Issue A) — does not create or modify anything.
export const checkDuplicateAFSJob = (clientId, financialYear) =>
  API.get(`/api/afs-jobs/check-duplicate`, { params: { client_id: clientId, financial_year: financialYear } })
export const downloadAFSJob   = (jobId)    => API.get(`/api/afs-jobs/${jobId}/download`, {
  responseType: 'blob',
})
// Phase 8C (July 2026) — Word (.docx) export, generated alongside the PDF
// during final AFS generation. 404s if the job predates this feature or the
// Word export failed non-fatally during generation — the PDF is unaffected
// either way.
export const downloadAFSJobWord = (jobId) => API.get(`/api/afs-jobs/${jobId}/download-word`, {
  responseType: 'blob',
})

// Phase 5 (Issue A) — step-boundary autosave/resume. saveJobProgress is
// called once per step transition (Trial Balance -> Additional Documents ->
// Notes -> Review), not on every keystroke — see SaveProgressRequest in
// main.py. resumeJobProgress rehydrates that saved state so a job can
// actually be picked back up instead of restarted from scratch.
export const saveJobProgress = (jobId, data) =>
  API.post(`/api/afs-jobs/${jobId}/save-progress`, data)
export const resumeJobProgress = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/resume`)

// Phase 5 (Issue B) — additional downloadable outputs from AFS History,
// alongside the AFS PDF itself.
export const getReviewReport = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/review-report`)
export const downloadReviewReportPdf = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/review-report/pdf`, { responseType: 'blob' })
export const downloadXBRLJob = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/xbrl`, { responseType: 'blob' })
export const downloadEngagementLetter = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/engagement-letter`, { responseType: 'blob' })
export const downloadRepresentationLetter = (jobId) =>
  API.get(`/api/afs-jobs/${jobId}/representation-letter`, { responseType: 'blob' })

// Phase 5 (Issue A) — explicitly abandon an in-progress draft so AFS History
// doesn't accumulate started-but-never-finished jobs with no way to remove
// them. Deliberately only works on non-complete jobs (enforced server-side).
export const discardDraftJob = (jobId) =>
  API.delete(`/api/afs-jobs/${jobId}`)


// ══════════════════════════════════════════════════════════════════════════════
// AI MAPPING & NOTES
// ══════════════════════════════════════════════════════════════════════════════
export const mapAccount       = (data) => API.post('/api/mapping/map-account', data)
export const generateNote     = (data) => API.post('/api/ai/generate-note', data)
export const generateAllNotes = (data) => API.post('/api/ai/generate-all-notes', data, {
  timeout: 180000,
})


// ══════════════════════════════════════════════════════════════════════════════
// TRIAL BALANCE & BANK STATEMENT UPLOAD
// ══════════════════════════════════════════════════════════════════════════════
export const parseTrialBalance = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return API.post('/api/trial-balance/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  })
}

export const parseBankStatement = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return API.post('/api/bank/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  })
}

export const categorizeTransaction = (data) =>
  API.post('/api/bank/categorize-transaction', data)


// ══════════════════════════════════════════════════════════════════════════════
// AFS GENERATION & VALIDATION
// ══════════════════════════════════════════════════════════════════════════════
export const generateAFS    = (data) => API.post('/api/afs/generate', data, {
  responseType: 'blob',
  timeout: 180000,
})
// Phase 7 — AFS Preview: watermarked, view-only, decoupled from credit
// deduction. Genuinely free/unlimited (never touches afs_count). Returns
// { pages: [base64 PNG, ...], page_count, watermarked } — rasterized page
// images, never real PDF bytes, so there's no data: URI/blob src an
// inspector could pull a clean document out of. Statements.jsx renders
// these as <img> elements in an on-screen viewer.
export const previewAFS     = (data) => API.post('/api/afs/preview', data, {
  timeout: 180000,
})
export const validateAFS    = (data) => API.post('/api/afs/validate', data)
export const generateReview = (data) => API.post('/api/afs/review', data)

// Persists the Review page's Disclosure Checklist server-side so
// /api/afs/generate can independently verify it was actually completed,
// rather than trusting the frontend's disabled-button state. Must succeed
// before the user is allowed to proceed to Statements/generation.
export const confirmDisclosureChecklist = (data) => API.post('/api/afs/checklist', data)

// Phase 3 — audit trail. Read-only: no update/delete endpoint exists for
// this on the backend, by design.
export const getAuditLog = (jobId) => API.get(`/api/afs/${jobId}/audit-log`)

// R3 — Deferred tax computation
// Accepts { figures, sub_accounts }, returns full SARS workings + traffic light.
// Also returned inline as data.deferred_tax from generateReview() above.
export const computeDeferredTax = (data) => API.post('/api/afs/deferred-tax', data)

// R7 — iXBRL export for CIPC regulatory submission
// Returns a downloadable .html iXBRL file (IFRS Taxonomy 2023, CIPC-compatible).
// Only surfaced for Pty Ltd and NPC entities in Statements.jsx.
export const exportXBRL = (data) => API.post('/api/afs/export-xbrl', data, {
  responseType: 'blob',
  timeout: 60000,
})

// R9 — Benchmark intelligence report
// Returns { industry, benchmarks, comparison[], narrative }
// Rendered by BenchmarkReport.jsx after PDF download in Statements.jsx.
export const generateBenchmark = (data) => API.post('/api/afs/benchmark', data)


// ══════════════════════════════════════════════════════════════════════════════
// R8 — CONSOLIDATIONS
// ══════════════════════════════════════════════════════════════════════════════
export const listConsolidations   = (firmId) => API.get(`/api/consolidations?firm_id=${firmId}`)
export const createConsolidation  = (data)   => API.post('/api/consolidations', data)
export const getConsolidation     = (id)     => API.get(`/api/consolidations/${id}`)
export const updateConsolidation  = (id, data) => API.put(`/api/consolidations/${id}`, data)
export const detectIntercompany   = (id)     => API.post(`/api/consolidations/${id}/detect`, {}, {
  timeout: 60000,
})
export const getEliminationHistory = (id)    => API.get(`/api/consolidations/${id}/elimination-history`)
export const updateEliminationHistoryYear = (id, financialYear, eliminations) =>
  API.put(`/api/consolidations/${id}/elimination-history/${financialYear}`, { eliminations })
// forceOverride mirrors the standalone AFS path's force_override — lets a
// preparer proceed past the R4 validate_figures() gate after reviewing the
// checks (Phase 1, 9 Sep 2026). Sent as a query param since this endpoint
// has never taken a request body.
export const generateConsolidated = (id, forceOverride = false) =>
  API.post(`/api/consolidations/${id}/generate?force_override=${forceOverride ? 'true' : 'false'}`, {}, {
    responseType: 'blob',
    timeout: 180000,
  })


// ══════════════════════════════════════════════════════════════════════════════
// PAYMENTS
// ══════════════════════════════════════════════════════════════════════════════
export const createSubscription = (data) => API.post('/api/payments/create-subscription', data)
export const createPayPerEngagementSignup = (data) => API.post('/api/payments/create-payg-signup', data)
export const createCreditTopup = (creditType, quantity) =>
  API.post('/api/payments/create-credit-topup', { credit_type: creditType, quantity })
export const getCreditSummary = () => API.get('/api/billing/credit')
export const switchPlanType = (firmId, planType) => API.post(`/api/firms/${firmId}/switch-plan-type`, { plan_type: planType })
export const createAddonSubscription = (addonKey) =>
  API.post('/api/payments/create-addon-subscription', { addon_key: addonKey })
export const cancelSubscription = () => API.post('/api/payments/cancel-subscription', {})
export const setReviewAddon = (enabled) => API.post('/api/billing/review-addon', { enabled })
export const resumeSubscription = () => API.post('/api/payments/resume-subscription', {})


// ══════════════════════════════════════════════════════════════════════════════
// SIGN-OFF  (R10 — OTP-verified director sign-off)
// ══════════════════════════════════════════════════════════════════════════════
// Accountant sends the sign-off link
export const sendSignOff = (data) => API.post('/api/signoff/send', data)

// Phase 2 — internal AFS review gate (opt-in per firm via
// firms.require_afs_review). Firms who haven't enabled it are unaffected;
// these calls simply won't matter to /api/afs/generate or /api/signoff/send.
export const getReviewStatus       = (jobId)       => API.get(`/api/afs/${jobId}/review-status`)
export const submitForReview       = (data)        => API.post('/api/afs/submit-for-review', data)

// Review (ISRE 2400 Revised) engagement file — one per client + financial year.
export const getReviewFileMeta       = ()     => API.get('/api/review-file/meta').then((r) => r.data)
export const getReviewFile           = (clientId, financialYear) =>
  API.get('/api/review-file', { params: { client_id: clientId, financial_year: financialYear } }).then((r) => r.data)
export const saveReviewSection       = (data) => API.post('/api/review-file/section', data).then((r) => r.data)
export const refreshReviewAnalytics  = (data) => API.post('/api/review-file/refresh-analytics', data).then((r) => r.data)
export const submitReviewFile        = (data) => API.post('/api/review-file/submit', data).then((r) => r.data)
export const decideReviewFile        = (data) => API.post('/api/review-file/decision', data).then((r) => r.data)
export const submitReviewDecision  = (data)        => API.post('/api/afs/review-decision', data)

// Public — director fetches their portal data by token (no Supabase session)
export const getSignOff = (token) => PublicAPI.get(`/api/signoff/${token}`)

// Step 1: request OTP to director's phone via SMS (Africa's Talking)
export const requestOTP = (token, data) =>
  PublicAPI.post(`/api/signoff/${token}/request-otp`, data)

// Step 2: verify OTP and record the electronic signature
export const verifyAndSign = (token, data) =>
  PublicAPI.post(`/api/signoff/${token}/verify-sign`, data)

// Legacy typed-name sign — kept for backwards compatibility
export const submitSignature = (token, data) =>
  PublicAPI.post(`/api/signoff/${token}/sign`, data)

// Accountant polls sign-off status
export const getSignoffStatus = (jobId) => API.get(`/api/signoff/status/${jobId}`)


// ══════════════════════════════════════════════════════════════════════════════
// WORKPAPERS
// ══════════════════════════════════════════════════════════════════════════════
export const createWorkpaper = (data) =>
  API.post('/api/workpapers', data, { timeout: 120000 })

export const getWorkpaper = (afsJobId) =>
  API.get(`/api/workpapers?afs_job_id=${afsJobId}`)

export const getWorkpaperById = (workpaperId) =>
  API.get(`/api/workpapers/${workpaperId}`)

export const updateWorkpaperLine = (workpaperId, lineId, data) =>
  API.put(`/api/workpapers/${workpaperId}/lines/${lineId}`, data)

export const addReviewPoint = (workpaperId, data) =>
  API.post(`/api/workpapers/${workpaperId}/review-points`, data)

export const updateReviewPoint = (workpaperId, pointId, data) =>
  API.put(`/api/workpapers/${workpaperId}/review-points/${pointId}`, data)

export const getSupporting = (workpaperId) =>
  API.get(`/api/workpapers/${workpaperId}/supporting`)

export const saveSupporting = (workpaperId, scheduleType, data) =>
  API.put(`/api/workpapers/${workpaperId}/supporting/${scheduleType}`, data)

export const addSignoff = (workpaperId, data) =>
  API.post(`/api/workpapers/${workpaperId}/signoff`, data)

export const completeWorkpaper = (workpaperId, data) =>
  API.post(`/api/workpapers/${workpaperId}/complete`, data)

// Completion Memo download (Sep 2026) — redownload the PDF persisted at
// lock time, same blob-via-signed-redirect pattern as the engagement/
// representation letter downloads above.
export const downloadCompletionMemo = (workpaperId) =>
  API.get(`/api/workpapers/${workpaperId}/completion-memo`, { responseType: 'blob' })


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR A — OFFLINE & RESILIENCE
// ══════════════════════════════════════════════════════════════════════════════
// Used by OfflineBanner (App.jsx polls every 30 seconds).
// Raw fetch so it works even if the axios instance is broken.
export const checkHealth = () =>
  fetch(`${BASE_URL}/health`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(8000),
  }).then((r) => r.json())


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR D1 — SOURCE DOCUMENT ENRICHMENT
// ══════════════════════════════════════════════════════════════════════════════

// Upload a source document and trigger AI extraction.
// onProgress(percent) is called during upload — use for a progress bar in
// AdditionalDocuments.jsx.
// Uses raw XHR (not axios) so upload progress events are available.
export const uploadSourceDocument = async (
  afsJobId,
  documentType,
  file,
  onProgress = null
) => {
  const token = await getBearerToken()
  const formData = new FormData()
  formData.append('afs_job_id', afsJobId)
  formData.append('document_type', documentType)
  formData.append('file', file)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}/api/documents/upload`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          resolve({})
        }
      } else {
        let detail = 'Upload failed'
        try {
          detail = JSON.parse(xhr.responseText)?.detail || detail
        } catch {}
        const err = new Error(detail)
        err.response = { status: xhr.status, data: { detail } }
        reject(err)
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(formData)
  })
}

// Fetch all source documents attached to an AFS job
export const getSourceDocuments = (afsJobId) =>
  API.get(`/api/documents/${afsJobId}`).then((r) => r.data)

// Delete a source document by its UUID
export const deleteSourceDocument = (documentId) =>
  API.delete(`/api/documents/item/${documentId}`).then((r) => r.data)

// Get-or-create a draft AFS job so documents/manual entries have a real
// afs_job_id to attach to before the AFS itself has been generated.
// Phase 1 (Jul 2026): periodStart/periodEnd are optional "D Month YYYY"
// strings from the free date-range picker — omitted, the backend falls back
// to the standard year_end_month/day-derived assumption exactly as before.
// Bank-statement draft-autosave fix: `method` tells the backend which
// pipeline is creating this draft ('trial_balance' default, or
// 'bank_statement') so the row's `method` column is correct from the
// moment it's first created, not just at final generation.
export const getOrCreateDraftJob = (clientId, firmId, financialYear, periodStart, periodEnd, method) =>
  API.post('/api/afs-jobs/get-or-create', {
    client_id: clientId, firm_id: firmId, financial_year: financialYear,
    period_start: periodStart || undefined, period_end: periodEnd || undefined,
    method: method || undefined,
  }).then((r) => r.data)

// Section 3 — manual entry alternative to document upload. Writes directly
// to afs_source_documents with entry_method='manual', same downstream effect
// as an uploaded+extracted document. firm_id is derived server-side from the
// authenticated JWT, not sent by the client.
export const saveManualDocument = (afsJobId, documentType, payload) =>
  API.post('/api/documents/manual', {
    afs_job_id: afsJobId, document_type: documentType, payload,
  }).then((r) => r.data)

// Per-note verification status map — used by Notes.jsx to render
// 🟢 Source-verified / 🟡 TB-derived / ⚪ AI-estimated badges
export const getVerificationStatus = (afsJobId) =>
  API.get(`/api/documents/${afsJobId}/verification-status`).then((r) => r.data)


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR D2 — ENGAGEMENT & MANAGEMENT REPRESENTATION LETTERS
// ══════════════════════════════════════════════════════════════════════════════
// Both return PDF blobs. Rendered via "Generate Letter" buttons in Statements.jsx.
// Can be routed through the existing sign-off portal for e-signature.
export const generateEngagementLetter = (data) =>
  API.post('/api/letters/engagement', data, {
    responseType: 'blob',
    timeout: 60000,
  })

export const generateRepresentationLetter = (data) =>
  API.post('/api/letters/representation', data, {
    responseType: 'blob',
    timeout: 60000,
  })


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR E — PRACTICE WORKFLOW
// ══════════════════════════════════════════════════════════════════════════════

// ── E1: CLIENT INTAKE PORTAL ──────────────────────────────────────────────────
// Accountant side (authenticated)
export const createIntakeRequest = (data)       => API.post('/api/intake', data)
export const getIntakeRequests   = (params)     => API.get('/api/intake', { params })
export const getIntakeRequest    = (id)         => API.get(`/api/intake/${id}`)
export const getIntakeDocument   = (id, itemId) => API.get(`/api/intake/${id}/document/${itemId}`)
export const sendIntakeReminder  = (id)         => API.post(`/api/intake/${id}/remind`)

// Public portal — client fills these in via /intake/:token (no Supabase session)
export const getIntakePortal   = (token) => PublicAPI.get(`/api/intake/portal/${token}`)
export const submitIntakeText  = (token, data) =>
  PublicAPI.post(`/api/intake/portal/${token}/text`, data)
export const submitIntakeFile  = (token, itemId, file) => {
  const formData = new FormData()
  formData.append('file', file)
  return PublicAPI.post(
    `/api/intake/portal/${token}/file?item_id=${itemId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
  )
}
export const submitIntakeComplete = (token) =>
  PublicAPI.post(`/api/intake/portal/${token}/submit`)

// ── E2: OPENING BALANCE RECONCILIATION ────────────────────────────────────────
export const reconcileOpeningBalances = (data) =>
  API.post('/api/afs/reconcile-opening', data, { timeout: 60000 })
export const reconcileAction = (data) => API.post('/api/afs/reconcile-action', data)

// ── E3: COMPLIANCE CALENDAR ────────────────────────────────────────────────────
export const generateComplianceItems = (clientId) =>
  API.post(`/api/compliance/generate?client_id=${clientId}`)
export const getComplianceItems   = (params)   => API.get('/api/compliance', { params })
export const addComplianceItem    = (data)     => API.post('/api/compliance', data)
export const updateComplianceItem = (id, data) => API.put(`/api/compliance/${id}`, data)
export const deleteComplianceItem = (id)       => API.delete(`/api/compliance/${id}`)
export const sendComplianceDigest = (firmId)   =>
  API.post(`/api/compliance/digest?firm_id=${firmId}`)

// ── E4: BATCH PROCESSING ───────────────────────────────────────────────────────
export const createBatch       = (data)   => API.post('/api/batch', data)
export const getBatches        = (firmId) => API.get(`/api/batch?firm_id=${firmId}`)
export const getBatch          = (id)     => API.get(`/api/batch/${id}`)
export const updateBatch       = (id, data) => API.put(`/api/batch/${id}`, data)
export const sendBatchSignoffs = (id, data) =>
  API.post(`/api/batch/${id}/signoff`, data, { timeout: 120000 })


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR B — ADMIN BOARD
// ══════════════════════════════════════════════════════════════════════════════

// ── B1: Plan Management ────────────────────────────────────────────────────────
export const getPlansConfig    = ()      => API.get('/api/admin/config/plans').then((r) => r.data)
export const updatePlansConfig = (plans) =>
  API.put('/api/admin/config/plans', { plans }).then((r) => r.data)

// Generic platform_config read/write (used for add-ons and any future config key)
export const adminGetConfig    = (key) =>
  API.get(`/api/admin/config/${key}`).then((r) => r.data)
export const adminUpdateConfig = (key, configValue) =>
  API.put(`/api/admin/config/${key}`, { config_value: configValue }).then((r) => r.data)

// ── B2: Firm Management ────────────────────────────────────────────────────────
export const adminListFirms      = (params = {}) =>
  API.get('/api/admin/firms', { params }).then((r) => r.data)
export const adminGetFirm        = (firmId) =>
  API.get(`/api/admin/firms/${firmId}`).then((r) => r.data)
export const adminResetUsage     = (firmId, afsCount = 0) =>
  API.put(`/api/admin/firms/${firmId}/reset-usage`, { afs_count: afsCount }).then((r) => r.data)
export const adminChangePlan     = (firmId, plan) =>
  API.put(`/api/admin/firms/${firmId}/plan`, { plan }).then((r) => r.data)
export const adminChangeSeats    = (firmId, userCount) =>
  API.put(`/api/admin/firms/${firmId}/seats`, { user_count: userCount }).then((r) => r.data)
export const adminSetFirmStatus  = (firmId, status) =>
  API.put(`/api/admin/firms/${firmId}/status`, { status }).then((r) => r.data)
export const adminVerifyEmail    = (firmId) =>
  API.put(`/api/admin/firms/${firmId}/verify-email`).then((r) => r.data)
export const adminSendVerificationLink = (firmId) =>
  API.post(`/api/admin/firms/${firmId}/send-verification-link`).then((r) => r.data)
export const adminSendFirmMessage = (firmId, subject, message) =>
  API.post(`/api/admin/firms/${firmId}/message`, { subject, message }).then((r) => r.data)
export const adminDeleteFirm     = (firmId, confirmationName) =>
  API.delete(`/api/admin/firms/${firmId}`, { data: { confirmation_name: confirmationName } }).then((r) => r.data)

// ── B2: AFS Jobs (admin view) ──────────────────────────────────────────────────
export const adminListJobs = (params = {}) =>
  API.get('/api/admin/afs-jobs', { params }).then((r) => r.data)

// ── B2: Support Log ────────────────────────────────────────────────────────────
export const getSupportLog  = (firmId) =>
  API.get(`/api/admin/support-log/${firmId}`).then((r) => r.data)
export const addSupportNote = (firmId, note) =>
  API.post('/api/admin/support-log', { note }, { params: { firm_id: firmId } }).then((r) => r.data)

// ── B2: Help Chat Escalations (operator inbox) ─────────────────────────────────
export const getEscalations = (unreadOnly = true) =>
  API.get('/api/admin/escalations', { params: { unread_only: unreadOnly } }).then((r) => r.data)
export const resolveEscalation = (entryId) =>
  API.put(`/api/admin/escalations/${entryId}/resolve`).then((r) => r.data)

// ── Phase 9: Practice Number Verification queue ────────────────────────────────
export const getPracticeNumberQueue = (status = 'pending') =>
  API.get('/api/admin/practice-number-queue', { params: { status } }).then((r) => r.data)
export const approvePracticeNumber = (entryId, adminNotes = '') =>
  API.post(`/api/admin/practice-number-queue/${entryId}/approve`, { admin_notes: adminNotes }).then((r) => r.data)
export const rejectPracticeNumber = (entryId, adminNotes = '') =>
  API.post(`/api/admin/practice-number-queue/${entryId}/reject`, { admin_notes: adminNotes }).then((r) => r.data)

// ── Phase 10: Statutory Rate Configuration ─────────────────────────────────────
// Admin Board — canonical, effective-dated, human-confirmed rate history.
export const getStatutoryRates = (rateType = null) =>
  API.get('/api/admin/statutory-rates', { params: rateType ? { rate_type: rateType } : {} }).then((r) => r.data)
export const confirmStatutoryRate = (pendingId, effectiveFrom = null) =>
  API.post(`/api/admin/statutory-rates/${pendingId}/confirm`, { effective_from: effectiveFrom }).then((r) => r.data)
export const dismissStatutoryRate = (pendingId, reason = '') =>
  API.post(`/api/admin/statutory-rates/${pendingId}/dismiss`, { reason }).then((r) => r.data)
export const addManualStatutoryRate = (rateType, rate, effectiveFrom, effectiveTo, notes = '') =>
  API.post('/api/admin/statutory-rates/manual', {
    rate_type: rateType, rate, effective_from: effectiveFrom, effective_to: effectiveTo, notes,
  }).then((r) => r.data)
// 22 Jul 2026 follow-up: edit an EXISTING confirmed/dismissed row in place
// (previously only add/confirm/dismiss existed — see kailux-master-plan.md §4 #12).
// Only pass the fields you want changed; omit the rest (leave as undefined/null).
export const editStatutoryRate = (rateId, { rate, effectiveFrom, effectiveTo, notes } = {}) =>
  API.post(`/api/admin/statutory-rates/${rateId}/edit`, {
    rate: rate ?? null, effective_from: effectiveFrom ?? null,
    effective_to: effectiveTo ?? null, notes: notes ?? null,
  }).then((r) => r.data)

// ── SBC (small business corporation) tax tables — kailux-fixes-log.md §104 ───────
// Admin Board: database-stored, year-end-keyed SBC tax tables with an audit trail and a
// coverage alert that warns before the latest table runs out.
export const getSbcTables = () =>
  API.get('/api/admin/sbc-tables').then((r) => r.data)
export const getSbcTablesCoverage = () =>
  API.get('/api/admin/sbc-tables/coverage').then((r) => r.data)
export const addSbcTable = ({ endsFrom, endsTo, bands, source, notes = '' }) =>
  API.post('/api/admin/sbc-tables', {
    ends_from: endsFrom, ends_to: endsTo, bands, source, notes,
  }).then((r) => r.data)
// Only pass the fields you want changed; omit the rest.
export const editSbcTable = (tableId, { endsFrom, endsTo, bands, source, notes } = {}) =>
  API.post(`/api/admin/sbc-tables/${tableId}/edit`, {
    ends_from: endsFrom ?? null, ends_to: endsTo ?? null, bands: bands ?? null,
    source: source ?? null, notes: notes ?? null,
  }).then((r) => r.data)
export const supersedeSbcTable = (tableId, reason) =>
  API.post(`/api/admin/sbc-tables/${tableId}/supersede`, { reason }).then((r) => r.data)

// §117: a small business corporation's deferred-tax RATE BASIS — 'statutory', 'entered' (rate is a
// FRACTION, 0.15 = 15%) or 'cleared'. Recorded with who/when and disclosed in the income tax note.
export const saveDeferredTaxRate = ({ clientId, financialYear, basis, rate, reason = '', yearEnd = '' }) =>
  API.post('/api/afs/deferred-tax-rate', {
    client_id: clientId, financial_year: financialYear, basis,
    rate: rate ?? null, reason, year_end: yearEnd,
  }).then((r) => r.data)

// AFS prep flow — per-job suggestion + confirm/override.
export const getTaxRateSuggestion = (clientId, financialYear, yearEnd = '') =>
  API.get('/api/afs/tax-rate-suggestion', { params: { client_id: clientId, financial_year: financialYear, year_end: yearEnd } }).then((r) => r.data)
export const confirmTaxRate = (clientId, financialYear, taxRate, wtaRateOverrides = {}) =>
  API.post('/api/afs/confirm-tax-rate', {
    client_id: clientId, financial_year: financialYear, tax_rate: taxRate, wta_rate_overrides: wtaRateOverrides,
  }).then((r) => r.data)
// 22 Jul 2026 follow-up: full confirmation history for one engagement — what
// rate was used for each past generation of this same job, not just today's
// current value (see kailux-master-plan.md §4 #12).
export const getJobTaxRateHistory = (clientId, financialYear) =>
  API.get('/api/afs/tax-rate-history', { params: { client_id: clientId, financial_year: financialYear } }).then((r) => r.data)

// Aug 2026: engagement-team going-concern override, mirrors confirmTaxRate's
// pattern. override: true forces a material-uncertainty conclusion, false
// forces no material uncertainty, null/undefined reverts to fully automatic
// (computed indicators decide). rationale is rendered directly into the
// Directors' Report and Note 20, so pass a real explanation when overriding.
export const setGoingConcernOverride = (clientId, financialYear, override, rationale = '') =>
  API.post('/api/afs/going-concern-override', {
    client_id: clientId, financial_year: financialYear, override, rationale,
  }).then((r) => r.data)

// Current override state for one engagement — override/rationale are both
// null when nothing has ever been set (fully automatic).
export const getGoingConcernStatus = (clientId, financialYear) =>
  API.get('/api/afs/going-concern-status', { params: { client_id: clientId, financial_year: financialYear } }).then((r) => r.data)


// ── Tax provision decision (29 Sep 2026, §88) ─────────────────────────────────
// A profit with no income tax at all silently presents pre-tax profit as the
// profit for the year. previewTaxProvision is stateless: does this set of
// figures show that gap, what would an indicative provision be (profit before
// tax x the engagement's rate), and has the preparer already answered?
// saveTaxProvisionDecision records the preparer's EXPLICIT answer
// ('added' + amount | 'declined' + reason | 'cleared') — it never changes
// figures; the caller applies an 'added' provision itself (utils/taxProvision).
export const previewTaxProvision = (clientId, financialYear, figures, yearEnd = '') =>
  API.post('/api/afs/tax-provision/preview', {
    client_id: clientId, financial_year: financialYear, figures, year_end: yearEnd,
  }).then((r) => r.data)

// §108: `basis` ('standard' | 'sbc') says which basis an 'added' provision rests on; omit it for the
// pre-§108 behaviour. 'sbc' needs sbcConfirmed === true (the preparer's explicit eligibility confirmation).
export const saveTaxProvisionDecision = ({ clientId, financialYear, decision, amount, reason, figures, yearEnd = '', basis, sbcConfirmed }) =>
  API.post('/api/afs/tax-provision', {
    client_id: clientId, financial_year: financialYear, decision,
    amount: amount ?? null, reason: reason || null, figures: figures || {}, year_end: yearEnd,
    basis: basis ?? null, sbc_confirmed: !!sbcConfirmed,
  }).then((r) => r.data)

// §108: the engagement's tax basis (standard company rate | small business corporation), the
// client-level "last confirmed as an SBC for FY…" hint, and whether an SBC table covers the year-end.
// Nothing here selects a basis; saveTaxBasis records the preparer's explicit choice
// ('standard' | 'sbc' + sbcConfirmed | 'cleared').
export const getTaxBasis = (clientId, financialYear, yearEnd = '') =>
  API.get('/api/afs/tax-basis', { params: { client_id: clientId, financial_year: financialYear, year_end: yearEnd } }).then((r) => r.data)
export const saveTaxBasis = ({ clientId, financialYear, basis, sbcConfirmed, yearEnd = '' }) =>
  API.post('/api/afs/tax-basis', {
    client_id: clientId, financial_year: financialYear, basis, sbc_confirmed: !!sbcConfirmed, year_end: yearEnd,
  }).then((r) => r.data)

export const getTaxProvisionStatus = (clientId, financialYear) =>
  API.get('/api/afs/tax-provision-status', { params: { client_id: clientId, financial_year: financialYear } }).then((r) => r.data)


// ── Private trusts (Oct 2026) ──────────────────────────────────────────────────
export const getTrustData = (clientId, financialYear) =>
  API.get(`/api/clients/${clientId}/trust-data/${encodeURIComponent(financialYear)}`).then((r) => r.data)

export const saveTrustData = (clientId, financialYear, { details, year }) =>
  API.put(`/api/clients/${clientId}/trust-data/${encodeURIComponent(financialYear)}`, { details, year }).then((r) => r.data)


// ── B2: Admin Stats ────────────────────────────────────────────────────────────
export const getAdminStats = () => API.get('/api/admin/stats').then((r) => r.data)

// ── B3: System Config & Feature Flags ──────────────────────────────────────────
export const getSystemConfig    = ()                      =>
  API.get('/api/admin/system').then((r) => r.data)
export const updateSystemConfig = (configKey, configValue) =>
  API.put('/api/admin/system', { config_key: configKey, config_value: configValue }).then((r) => r.data)
export const toggleFeatureFlag  = (flag, enabled)        =>
  API.put(`/api/admin/system/flags/${flag}`, { flag, enabled }).then((r) => r.data)

// ── Status page — scheduled maintenance (admin) ─────────────────────────────
// See scripts/status_monitor.py for why the started/completed transition
// emails are NOT triggered from here — only "scheduled" is, since that's
// the one event tied directly to this admin action.
export const getStatusComponents = () =>
  API.get('/api/admin/status/components').then((r) => r.data)
export const getStatusIncidents = () =>
  API.get('/api/admin/status/incidents').then((r) => r.data)
export const getStatusErrorEvents = () =>
  API.get('/api/admin/status/error-events').then((r) => r.data)
export const createStatusIncident = (payload) =>
  API.post('/api/admin/status/incidents', payload).then((r) => r.data)
export const addStatusIncidentUpdate = (incidentId, payload) =>
  API.post(`/api/admin/status/incidents/${incidentId}/updates`, payload).then((r) => r.data)
export const listMaintenanceWindows = () =>
  API.get('/api/admin/status/maintenance-windows').then((r) => r.data)
export const createMaintenanceWindow = (payload) =>
  API.post('/api/admin/status/maintenance-windows', payload).then((r) => r.data)
export const updateMaintenanceWindow = (id, payload) =>
  API.put(`/api/admin/status/maintenance-windows/${id}`, payload).then((r) => r.data)
export const cancelMaintenanceWindow = (id) =>
  API.delete(`/api/admin/status/maintenance-windows/${id}`).then((r) => r.data)

// Public — read by the app on load to conditionally show/hide features
export const getFeatureFlags = () => API.get('/api/config/feature-flags').then((r) => r.data)
// Unauthenticated list prices for the Signup page (it has no session, so it
// can't read /api/admin/config/seat_pricing). Best-effort: silent on failure
// and short-timeout, because Signup falls back to its built-in prices.
export const getPublicPricing = () =>
  PublicAPI.get('/api/public/pricing', { silent: true, timeout: 8000 }).then((r) => r.data)
export const getAnnouncement = () => API.get('/api/config/announcement').then((r) => r.data)

// ── Admin -> client-app SSO (30 Aug 2026) ────────────────────────────────────
// mintAdminSsoToken: called from admin.kailux.co.za (Admin Board), needs the
// admin's own session — the request interceptor above attaches it.
// exchangeAdminSsoToken: called from app.kailux.co.za's public /sso route,
// BEFORE any session exists there — deliberately hits /api/auth/sso/exchange,
// which is public because the token itself is the credential (see main.py).
export const mintAdminSsoToken = () => API.post('/api/admin/sso/mint').then((r) => r.data)
export const exchangeAdminSsoToken = (token) =>
  API.post('/api/auth/sso/exchange', { token }).then((r) => r.data)


// ══════════════════════════════════════════════════════════════════════════════
// PILLAR F — RECURRING REVENUE
// ══════════════════════════════════════════════════════════════════════════════

// ── F1: Management Accounts ────────────────────────────────────────────────────
export const generateManagementAccounts = (payload) =>
  API.post('/api/management-accounts/generate', payload).then((r) => r.data)

export const getManagementAccountsHistory = (clientId) =>
  API.get('/api/management-accounts/history', { params: { client_id: clientId } }).then((r) => r.data)

export const redownloadManagementAccounts = (jobId) =>
  API.get(`/api/management-accounts/history/${jobId}/pdf`).then((r) => r.data)

export const redownloadManagementAccountsWord = (jobId) =>
  API.get(`/api/management-accounts/history/${jobId}/word`).then((r) => r.data)

// Draft persistence / resume (Sep 2026) — mirrors getOrCreateDraftJob /
// saveJobProgress / resumeJobProgress for AFS. Called once client+period are
// known (end of step 2, Engagement), then autosaved continuously through
// steps 3-5 so closing the tab, a crash, or a session timeout doesn't lose
// an in-progress pack — see 026_management_accounts_drafts_and_documents.sql.
export const getOrCreateMAJob = (clientId, period, entityType) =>
  API.post('/api/management-accounts/jobs/get-or-create', {
    client_id: clientId, period, entity_type: entityType || undefined,
  }).then((r) => r.data)

export const saveMAJobProgress = (jobId, data) =>
  API.post(`/api/management-accounts/jobs/${jobId}/save-progress`, data).then((r) => r.data)

export const resumeMAJobProgress = (jobId) =>
  API.get(`/api/management-accounts/jobs/${jobId}/resume`).then((r) => r.data)

export const listMAJobDrafts = (clientId) =>
  API.get('/api/management-accounts/jobs/drafts', { params: { client_id: clientId } }).then((r) => r.data)

export const getMASourceDocuments = (maJobId) =>
  API.get(`/api/management-accounts/documents/${maJobId}`).then((r) => r.data)

// Source-document enrichment — extracts figures from a trial balance, bank
// statement, fixed asset register, debtors/creditors age analysis, loan
// agreement, or prior year AFS, so figures don't have to be typed in by
// hand. Stateless on the backend UNLESS maJobId is passed, in which case the
// extraction result is also persisted so a resumed session can re-derive
// this same pre-fill via getMASourceDocuments without re-uploading.
export const extractManagementAccountsDocument = async (documentType, file, onProgress = null, maJobId = null) => {
  const token = await getBearerToken()
  const formData = new FormData()
  formData.append('document_type', documentType)
  formData.append('file', file)
  if (maJobId) formData.append('ma_job_id', maJobId)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}/api/management-accounts/documents/extract`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)) } catch { resolve({}) }
      } else {
        let detail = 'Document extraction failed'
        try { detail = JSON.parse(xhr.responseText)?.detail || detail } catch {}
        const err = new Error(detail)
        err.response = { status: xhr.status, data: { detail } }
        reject(err)
      }
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(formData)
  })
}

// ── F2: Add-on usage (firm-facing — used by Settings → Subscription) ───────────
export const getAddonUsageSummary = () =>
  API.get('/api/billing/addon-usage').then((r) => r.data)


export default API
// ── Audit Phase 0 (Oct 2026) ─────────────────────────────────────────────────
// Registered Auditor credentials, requirement check, prepaid credits, audit
// engagements. Every rule is enforced by the server; these only carry requests.
export const getAuditOverview = () => API.get('/api/audit/overview').then((r) => r.data)
export const submitAuditCredential = (fields, files = []) => {
  const fd = new FormData()
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v) })
  fd.set('attested', fields.attested ? 'true' : 'false')
  files.forEach((f) => fd.append('files', f))
  return API.post('/api/audit/credentials', fd).then((r) => r.data)
}
export const getAuditAttestationStatement = (credentialType, registrationNumber = '') =>
  API.get('/api/audit/attestation-statement', { params: { credential_type: credentialType, registration_number: registrationNumber } }).then((r) => r.data)
export const renewAuditCredential = (id) => API.post(`/api/audit/credentials/${id}/renew`, { attested: true }).then((r) => r.data)
export const withdrawAuditCredential = (id, reason) => API.post(`/api/audit/credentials/${id}/withdraw`, { reason: reason || null }).then((r) => r.data)
export const createAssurance = (data) => API.post('/api/audit/assessments', data).then((r) => r.data)
export const listAssurance = (clientId) =>
  API.get('/api/audit/assessments', { params: { client_id: clientId } }).then((r) => r.data)
export const confirmAssurance = (id) =>
  API.post(`/api/audit/assessments/${id}/confirm`, { acknowledged: true }).then((r) => r.data)
export const getAuditCredits = () => API.get('/api/audit/credits').then((r) => r.data)
export const purchaseAuditCredits = (quantity) =>
  API.post('/api/audit/credits/purchase', { quantity }).then((r) => r.data)
export const createAuditEngagement = (data) => API.post('/api/audit/engagements', data).then((r) => r.data)
export const listAuditEngagements = (clientId) =>
  API.get('/api/audit/engagements', { params: clientId ? { client_id: clientId } : {} }).then((r) => r.data)
export const getAuditEngagement = (id) => API.get(`/api/audit/engagements/${id}`).then((r) => r.data)
export const assignAuditMember = (id, firmUserId, role) =>
  API.post(`/api/audit/engagements/${id}/members`, { firm_user_id: firmUserId, role }).then((r) => r.data)
export const removeAuditMember = (id, memberId) =>
  API.delete(`/api/audit/engagements/${id}/members/${memberId}`).then((r) => r.data)
export const setAuditOptions = (id, data) => API.put(`/api/audit/engagements/${id}/options`, data).then((r) => r.data)
export const setAuditSection = (id, key, status, content) =>
  API.put(`/api/audit/engagements/${id}/sections/${key}`, { status, content }).then((r) => r.data)
export const recordAuditSignoff = (id, level, signerName, note) =>
  API.post(`/api/audit/engagements/${id}/signoffs`, { level, signer_name: signerName, note: note || null }).then((r) => r.data)
export const issueAuditReport = (id, reportDate) =>
  API.post(`/api/audit/engagements/${id}/issue`, { report_date: reportDate }).then((r) => r.data)
export const assembleAuditFile = (id) => API.post(`/api/audit/engagements/${id}/assemble`).then((r) => r.data)
export const addAuditAddition = (id, sectionKey, content, reason) =>
  API.post(`/api/audit/engagements/${id}/additions`, { section_key: sectionKey, content, reason }).then((r) => r.data)
export const approveAuditAddition = (id, additionId) =>
  API.post(`/api/audit/engagements/${id}/additions/${additionId}/approve`).then((r) => r.data)
export const verifyAuditChain = (id) => API.get(`/api/audit/engagements/${id}/verify-chain`).then((r) => r.data)
// Admin Board
export const adminRevokeAuditCredential = (id, reason) =>
  API.post(`/api/admin/audit/credentials/${id}/revoke`, { reason }).then((r) => r.data)
export const adminGrantAuditCredits = (firmId, credits, reason) =>
  API.post('/api/admin/audit/credits/grant', { firm_id: firmId, credits, reason }).then((r) => r.data)
export const adminGetAuditPricing = () => API.get('/api/admin/audit/pricing').then((r) => r.data)
export const adminSetAuditPricing = (price) =>
  API.put('/api/admin/audit/pricing', { price_per_credit_rand: price }).then((r) => r.data)
